
import {
  defineComponent,
  ref,
  onMounted,
  computed,
  watch,
  onBeforeUnmount,
} from "vue";
// import ProductCard from "@/components/Marketplace/ProductCard.vue";
import MarketplaceService from "@/services/marketplace";
import { useRoute, useRouter } from "vue-router";
import type { Product, Category } from "@/services/marketplace";
import { useStore } from "vuex";
import productCategoriesService from "@/services/products";
import NewProductCard from "@/components/Marketplace/NewProductCard.vue";

export default defineComponent({
  name: "MarketplacePage",
  components: {
    // ProductCard,
    NewProductCard,
  },
  setup() {
    const selectedSort = ref("popularity");
    const selectedOrgTypes = ref<string[]>([]);
    const selectedRegion = ref("");
    const minPrice = ref(null);
    const maxPrice = ref(null);
    const appliedFilters = ref<string[]>([]);
    const searchQuery = ref("");

    const products = ref<Product[]>([]);
    const route = useRoute();
    const store = useStore();
    const categories = ref<Category[]>([]);
    const selectedCategories = ref<string[]>([]);
    const selectedCategoryId = ref<string | null>(null);

    const isOpen = ref(false);
    const onlyVerified = ref(false);
    const onlyInStock = ref(false);
    const activeDropdown = ref<string | null>(null);
    const name = ref("");
    const hoveredCategory = ref<Category | null>(null);
    const isClicked = ref(false);
    const activeParent = ref<Category | null>(null);
    const activeChild = ref<Category | null>(null);
    // const marketSearchQuery = computed(() => route.query.search || "");
    const router = useRouter();
    // const activeCategoryName = computed(() => route.query.category__in || "");
    const regionInput = ref<string>("");
    const regions = ref<{ id: string; name: string }[]>([]);
    const selectedRegionIds = ref<string | number>();

    const pageMeta = computed(() => store.state.pageMeta.meta);

    const dropdownPanels = ref<HTMLElement | null>(null);

    const getRegions = async () => {
      try {
        MarketplaceService.getRegions().then((res) => {
          regions.value = res;
        });
      } catch (e) {
        console.error("Ошибка загрузки регионов", e);
      }
    };

    const applyRegion = async () => {
      const q: Record<string, any> = { ...route.query };

      if (selectedRegionIds.value) {
        q.regions__in = selectedRegionIds.value;
      } else {
        delete q.regions__in;
      }

      await router.push({
        name: "Marketplace",
        params: { category: route.params.category as string | undefined },
        query: q,
      });
      await loadByRoute(); // ← ключевая строка
      activeDropdown.value = null;
    };

    const clearRegion = async () => {
      selectedRegionIds.value = "";
      const q: Record<string, any> = { ...route.query };
      delete q.regions__in;
      await router.push({ name: "Marketplace", query: q });
      await loadByRoute(); // ← ключевая строка
      activeDropdown.value = null;
    };

    const toggleDropdown = (type: string) => {
      activeDropdown.value = activeDropdown.value === type ? null : type;
      if (activeDropdown.value === "region") {
        const fromUrl = (route.query.company__region__name__in as string) || "";
        regionInput.value = fromUrl;
      }
    };

    const closeDropdown = () => {
      activeDropdown.value = null;
    };

    const handleClickOutside = (event: MouseEvent) => {
      if (!activeDropdown.value) return;

      const target = event.target as Node | null;
      if (!target || !dropdownPanels.value) return;

      if (!dropdownPanels.value.contains(target)) {
        activeDropdown.value = null;
      }
    };

    const getCategoryName = (categoryId: string) => {
      const findCategoryById = (items: Category[]): Category | null => {
        for (const item of items) {
          if (item.id == categoryId || item.name == categoryId) return item;
          if (item.childrens) {
            const found = findCategoryById(item.childrens);
            if (found) return found;
          }
        }
        return null;
      };

      const category = findCategoryById(categories.value);
      return category ? category.name : "Неизвестная категория";
    };

    const getCategories = () => {
      productCategoriesService.all().then((res) => {
        categories.value = res;
      });
    };

    const handleCategoryClick = (category: Category) => {
      // if (category.childrens?.length) return; // выбираем только "лист"
      selectedCategoryId.value = String(category.id);
      router.push({
        name: "Marketplace",
        params: { category: category.slug },
        query: { ...route.query },
      });

      // если это лист — закрываем дропдаун; если есть дети — оставляем открытым
      if (!category.childrens?.length) {
        activeDropdown.value = null;
      } else {
        selectedCategoryId.value = category.id; // локально помним выбранную
        router.push({
          name: "Marketplace",
          params: { category: category.slug },
          query: { ...route.query },
        }); // только URL
        activeDropdown.value = null;
      }
    };

    const handleCategorySelect = (category: Category) => {
      activeDropdown.value = null;
    };

    // Логика фильтрации
    const filteredProducts = computed(() => {
      let filtered = products.value.filter(
        (product) => product.is_published // ← добавил фильтр по публикации
      );

      // Фильтрация по стоимости
      if (minPrice.value !== null) {
        filtered = filtered.filter(
          (product) => product.price >= (minPrice.value || 0)
        );
      }
      if (maxPrice.value !== null) {
        filtered = filtered.filter(
          (product) => product.price <= (maxPrice.value || 0)
        );
      }

      // Сортировка
      if (selectedSort.value === "price-asc") {
        filtered.sort((a, b) => a.price - b.price);
      } else if (selectedSort.value === "price-desc") {
        filtered.sort((a, b) => b.price - a.price);
      } else if (selectedSort.value === "popularity") {
        filtered.sort((a, b) => b.rating - a.rating);
      }
      return filtered;
    });

    const getProducts = () => {
      const page = parseInt(route.query.page as string, 10) || 1;
      MarketplaceService.all(page).then((res) => {
        products.value = res;
      });
    };

    const updateFilters = () => {
      appliedFilters.value = [];

      // Добавление выбранных форм организации
      if (selectedOrgTypes.value.length > 0) {
        appliedFilters.value.push(...selectedOrgTypes.value);
      }

      // Добавление диапазона цен
      if (minPrice.value !== null) {
        appliedFilters.value.push(`От ${minPrice.value}₽`);
      }
      if (maxPrice.value !== null) {
        appliedFilters.value.push(`До ${maxPrice.value}₽`);
      }
    };

    const findById = (
      items: Category[],
      id: string | number
    ): Category | null => {
      for (const item of items) {
        if (item.id === id || String(item.id) === String(id)) return item;
        if (item.childrens) {
          const found = findById(item.childrens, id);
          if (found) return found;
        }
      }
      return null;
    };

    const removeFilter = async (filter: string) => {
      const name = getCategoryName(String(filter));

      // updateFilters();
      const slugInUrl = route.params.category as string | undefined;

      const isCategoryRemoval =
        !filter ||
        filter === "category" ||
        (selectedCategoryId.value &&
          filter === String(selectedCategoryId.value));

      if (isCategoryRemoval) {
        if (slugInUrl) {
          await router.push({
            name: "Marketplace",
            params: { category: undefined },
            query: route.query,
          });
        }
        selectedCategoryId.value = null;
        await loadByRoute(); // ← учитывает regions__in
        return;
      }

      // 2) Снятие диапазона цен (если чипы у тебя формируются «От X₽» / «До Y₽»)
      if (filter && filter.startsWith("От")) {
        minPrice.value = null;
      }
      if (filter && filter.startsWith("До")) {
        maxPrice.value = null;
      }

      if (filter === "region" || filter === selectedRegionIds.value) {
        const q: Record<string, any> = { ...route.query };
        delete q.regions__in;
        await router.push({
          name: "Marketplace",
          params: route.params,
          query: q,
        });
        selectedRegionIds.value = "";
        regionInput.value = "";
        await loadByRoute();
        return;
      }

      // 3) Снятие региона
      // if (filter && filter === selectedRegion.value) {
      //   selectedRegion.value = "";
      // }

      // 4) Снятие типов организаций (если чипами выводишь элементы из selectedOrgTypes)
      if (filter && selectedOrgTypes.value.includes(filter)) {
        selectedOrgTypes.value = selectedOrgTypes.value.filter(
          (f) => f !== filter
        );
      }

      // 5) Обновить список «чипов»
      updateFilters();

      // 6) Перезагрузить товары по текущему состоянию (опционально)
      // Если у тебя товары подтягиваются реактивно по watch(route.params.category),
      // то здесь можно просто выйти. Если нужно вручную:
      if (!route.params.category) {
        getProducts();
      }
    };

    const onSearchByRegions = () => {
      if (searchQuery.value) {
        store.dispatch("companies/searchProducts", searchQuery.value);
      }
    };
    const preventNegativeInput = (event: any) => {
      if (event.key === "-" || event.key === "e") {
        event.preventDefault();
      }
    };
    const loadProductsBySlug = async (categorySlug?: string | string[]) => {
      if (!categorySlug) {
        getProducts();
        selectedCategoryId.value = null;
        return;
      }

      const slug = Array.isArray(categorySlug) ? categorySlug[0] : categorySlug;

      const findBySlug = (items: Category[], s: string): Category | null => {
        for (const it of items) {
          if (it.slug === s) return it;
          if (it.childrens) {
            const f = findBySlug(it.childrens, s);
            if (f) return f;
          }
        }
        return null;
      };

      const categoryObj = findBySlug(categories.value, slug);
      if (!categoryObj) return; // подождём загрузку категорий (см. watch ниже)

      // ВАЖНО: на бек — по name (как раньше category__in=name)
      const productsResult = await MarketplaceService.similar(categoryObj.name);
      products.value = productsResult;
      selectedCategoryId.value = categoryObj.id;
    };

    const loadByRoute = async () => {
      // slug категории
      const slug = Array.isArray(route.params.category)
        ? route.params.category[0]
        : (route.params.category as string | undefined);

      // regions__in из query
      const regionsIn = Array.isArray(route.query.regions__in)
        ? route.query.regions__in.join(",")
        : (route.query.regions__in as string | undefined);

      const params: {
        category__in?: string;
        company__region__name__in?: string;
      } = {};

      if (slug) {
        if (!categories.value.length) await getCategories();

        const findBySlug = (items: Category[], s: string): Category | null => {
          for (const it of items) {
            if (it.slug === s) return it;
            if (it.childrens) {
              const f = findBySlug(it.childrens, s);
              if (f) return f;
            }
          }
          return null;
        };

        const cat = findBySlug(categories.value, slug);
        if (cat) {
          params.category__in = cat.name; // бек ждёт name
          selectedCategoryId.value = String(cat.id);
        }
      }

      if (regionsIn) {
        params.company__region__name__in = regionsIn;
        selectedRegionIds.value = regionsIn;
      }

      // если нет ни категории, ни региона → дефолтная выдача
      if (!params.category__in && !params.company__region__name__in) {
        return getProducts();
      }

      products.value = await MarketplaceService.search(params);
    };

    watch(categories, () => {
      loadByRoute();
    });

    watch(
      () => [route.params.category, route.query.regions__in],
      () => {
        loadByRoute();
      },
      { immediate: true }
    );

    watch(categories, () => {
      loadByRoute();
    });

    onMounted(async () => {
      document.addEventListener("click", handleClickOutside);
      await Promise.all([getCategories(), getRegions()]);
      await loadByRoute();
    });

    onBeforeUnmount(() => {
      document.removeEventListener("click", handleClickOutside);
    });

    watch(
      [selectedOrgTypes, selectedRegion, minPrice, maxPrice],
      updateFilters,
      {
        immediate: true,
      }
    );

    return {
      searchQuery,
      appliedFilters,
      selectedSort,
      products,
      filteredProducts,
      minPrice,
      maxPrice,
      selectedCategories,
      categories,
      isOpen,
      onlyVerified,
      onlyInStock,
      activeDropdown,
      selectedRegion,
      name,
      hoveredCategory,
      isClicked,
      activeParent,
      activeChild,
      selectedCategoryId,
      regionInput,
      regions,
      selectedRegionIds,
      pageMeta,
      toggleDropdown,
      removeFilter,
      getProducts,
      onSearchByRegions,
      preventNegativeInput,
      getCategoryName,
      updateFilters,
      closeDropdown,
      handleCategorySelect,
      handleCategoryClick,
      applyRegion,
      clearRegion,
      dropdownPanels,
    };
  },
});
